<script setup>
  import {computed, inject, onBeforeUnmount, onMounted, ref, watch} from "vue";

  import ComponentBreadcrumb from "@components/Breadcrumb/Breadcrumb.vue";
  import ComponentBmpImportDialog from "@components/Dialogs/BmpImportDialog/BmpImportDialog.vue";
  import ComponentBmpStoreDialog from "@components/Dialogs/BmpStoreDialog.vue";
  import ComponentConfirmationDialog from "@components/Dialogs/ConfirmationDialog.vue";
  import ComponentJsonImportDialog from "@components/Dialogs/JsonImportDialog.vue";
  import ComponentMergeRecordDialog from "@components/Dialogs/MergeRecordDialog.vue";
  import ComponentRecordEditLabelDialog from "@components/Dialogs/RecordEditLabelDialog.vue";
  import ComponentRecordShareDialog from "@components/Dialogs/RecordShareDialog.vue";
  import ComponentRecordShareStatusDialog from "@components/Dialogs/RecordShareStatusDialog.vue";
  import ComponentDropdown from "@components/Dropdown/Dropdown.vue";
  import ComponentEmptyState from "@components/EmptyState/EmptyState.vue";
  import ComponentFilterFlags from "@components/Filter/FilterFlags.vue";
  import ComponentTodoList from "@components/Lists/TodoList.vue";
  import ComponentPagination from "@components/Pagination/Pagination.vue";
  import ComponentPatientSidebar from "@components/Sidebars/PatientSidebar.vue";
  import {router, useForm, usePage} from "@inertiajs/vue3";
  import PatientsDocumentHead from "@pages/Patients/Components/DocumentHead.vue";
  import PatientsDocumentRecordRow from "@pages/Patients/Components/DocumentRecordRow.vue";
  import ComponentRecordStatus from "@pages/Records/Components/RecordStatus.vue";
  import {MedButton, MedIcon, MedSpinner} from "@pharma4u/medicheck-components";
  import {cn} from "@pharma4u/medicheck-components/utils";
  import {debounce} from "es-toolkit";

  defineOptions({
    name: "PagesPatientShow",
  });

  const props = defineProps({
    patient: {
      type: Object,
      required: true,
    },
    records: {
      type: Object,
      required: true,
    },
    filters: {
      type: Object,
      required: true,
    },
  });

  const privacy = inject("$privacy");
  const broadcast = inject("$broadcast");

  const page = usePage();

  const refBmpImportDialog = ref(null);
  const refBmpStoreDialog = ref(null);
  const refMergeRecordDialog = ref(null);
  const refConfirmDeleteItem = ref(null);
  const refEditRecordLabelDialog = ref(null);
  const refJsonImportDialog = ref(null);
  const refShareRecordDialog = ref(null);
  const refShareStatusDialog = ref(null);

  const refConfirmBulkStatusChange = ref(null);
  const refBulkStatusChangeStatus = ref(null);

  const confirmDeleteTitle = ref("");
  const confirmDeleteContent = ref("");

  const selectedRecords = ref([]);

  const availableRecords = computed(() => Object.values(props.records.data).map((record) => record.id));

  let form = useForm({
    order: page.props?.filters?.order || "-updated_at",
    flags: page.props?.filters?.flags || [],
  });

  const availableFlags = {
    progress: {
      label: "Fortschritt",
      nullable: false,
      options: {
        formFilled: "Eingabe in Arbeit",
        resultCreated: "Analyse durchgeführt",
        resultSent: "Dokumentation vorhanden",
      },
    },
    status: {
      label: "Status",
      nullable: false,
      options: {
        default: "offen",
        inProgress: "in Bearbeitung",
        completed: "abgeschlossen",
        discarded: "irrelevant",
      },
    },
    createdWithinMonth: "innerhalb eines Monats erstellt",
    createdWithinYear: "innerhalb eines Jahres erstellt",
    createdOverAYear: "vor über einem Jahr erstellt",
  };

  watch(
    () => form.flags,
    debounce(function () {
      submitFilter();
    }, 500),
    {deep: true}, // required when watching arrays
  );

  watch(
    () => form.order,
    debounce(function () {
      submitFilter();
    }, 500),
  );

  onMounted(() => {
    broadcast.onMessage(handleBroadcastMessage);
  });

  onBeforeUnmount(() => {
    broadcast.offMessage(handleBroadcastMessage);
  });

  function createPolymedication() {
    router.post(route("records.store", {patient: props.patient.id}));
  }

  async function openBmpImportDialog() {
    refBmpImportDialog.value.open(await privacy.decryptPatient(props.patient));
  }

  async function openJsonImportDialog() {
    refJsonImportDialog.value.open(await privacy.decryptPatient(props.patient));
  }

  const handleBroadcastMessage = (data) => {
    if (data.action === "reload.records") {
      router.reload();
    }
  };

  function submitFilter() {
    form.get(route("patients.show", {patient: props.patient.id}), {
      preserveState: true,
      preserveScroll: true,
      replace: true,
    });
  }

  function confirmDeleteItem(recordIds) {
    confirmDeleteTitle.value = recordIds.length === 1 ? "Medikationsanalyse löschen?" : "Medikationsanalysen löschen?";
    confirmDeleteContent.value =
      recordIds.length === 1
        ? "Die Medikationsanalyse wird mit ggf. vorhandenem Analyseergebnis endgültig gelöscht und kann nicht wiederhergestellt werden."
        : "Die Medikationsanalysen (" +
          recordIds.length +
          ") werden mit ggf. vorhandenen Analyseergebnissen endgültig gelöscht und können nicht wiederhergestellt werden.";
    refConfirmDeleteItem.value.open(recordIds);
  }

  function confirmBulkStatusChange(recordIds) {
    refConfirmBulkStatusChange.value.open(recordIds);
  }

  function confirmMergeRecord(recordIds) {
    refMergeRecordDialog.value.open(
      props.patient,
      // filter records to selected records only
      props.records.data.filter((record) => {
        return recordIds.includes(record.id);
      }),
    );
  }

  function remove(recordIds) {
    selectedRecords.value = [];

    router.delete(route("records.destroy"), {
      preserveScroll: true,
      data: {ids: recordIds},
      optimistic: (pageProperties) => ({
        records: {
          ...pageProperties.records,
          data: pageProperties.records.data.filter((r) => !recordIds.includes(r.id)),
        },
      }),
      onSuccess: () => {
        for (const recordId of recordIds) {
          broadcast.record.postMessage(recordId, {action: "delete"});
        }
        broadcast.postMessage({action: "reload.records"});
      },
    });
  }

  function bulkStatusChange(recordIds, status) {
    router.patch(
      route("records.bulk-status-change"),
      {
        ids: recordIds,
        status: status,
      },
      {
        preserveScroll: true,
        only: ["flash", "errors", "records"],
      },
    );
  }

  function openShareRecord(record) {
    privacy.whenCryptReady(() => {
      return privacy
        .getCryptObject()
        .decrypt(record.info)
        .then((decrypted) => {
          record.info = decrypted;
          refShareRecordDialog.value.open(record);
        });
    });
  }

  function openShareStatus(record) {
    privacy.whenCryptReady(() => {
      return privacy
        .getCryptObject()
        .decrypt(record.info)
        .then((decrypted) => {
          record.info = decrypted;
          refShareStatusDialog.value.open(record);
        });
    });
  }

  function openEditRecordLabel(record) {
    refEditRecordLabelDialog.value.open(record);
  }
</script>

<template>
  <ComponentBreadcrumb
    class="mt-2"
    :links="[
      {
        label: 'Patienten',
        url: patient.is_fake ? $route('patients.index', {filter: 'shared-with-me'}) : $route('patients.index'),
      },
      {encryptedLabel: [patient.firstname, patient.lastname]},
    ]"
  />

  <section class="relative mt-6 flex items-start space-x-6">
    <div class="sticky top-20 z-10">
      <ComponentPatientSidebar :dynamic-width="false" :patient="patient" />
    </div>

    <article
      class="flex flex-1 flex-col items-start space-y-6 2xl:flex-row 2xl:items-start 2xl:space-y-0 2xl:space-x-6"
    >
      <!-- article::main -->
      <div class="w-full bg-white p-4 shadow-sm">
        <div class="flex items-center justify-between">
          <h2 v-if="patient.is_fake" class="text-2xl font-semibold">Geteilte Medikationsanalyse</h2>
          <h2 v-else class="text-2xl font-semibold">Medikationsanalysen</h2>

          <!-- TODO - Later change for now we style it like the new buttons-->
          <ComponentDropdown
            v-if="$page.props.can.create_records && !patient.is_fake"
            :class="
              cn(
                'med-outset-focus flex h-fit min-w-24 cursor-pointer items-center justify-center gap-2 rounded-md border-2 border-transparent bg-mcred-500 px-3 py-1.25 text-white drop-shadow-lg transition-colors hover:bg-mcred-450 hover:drop-shadow-none',
              )
            "
            :test-id="getTestId('new-analyse')"
          >
            <template #button>
              Neue Analyse
              <MedIcon icon="expand_more" />
            </template>

            <template #dropdown>
              <ul class="flex-row space-y-px">
                <li>
                  <MedButton
                    variant="primary"
                    flat
                    icon="edit"
                    rounded="none"
                    full-width
                    class="whitespace-nowrap"
                    align="left"
                    :data-test="getTestId('dropdown-create-poly-medication-button')"
                    @click="createPolymedication()"
                  >
                    Medikationsanalyse manuell eingeben
                  </MedButton>
                </li>

                <li>
                  <MedButton
                    variant="primary"
                    flat
                    icon="barcode_reader"
                    rounded="none"
                    full-width
                    align="left"
                    class="whitespace-nowrap"
                    :data-test="getTestId('dropdown-open-bmp-import-dialog-button')"
                    @click="openBmpImportDialog()"
                  >
                    Medikationsanalyse aus BMP importieren
                  </MedButton>
                </li>

                <li v-if="$page.props.can.json_import">
                  <MedButton
                    variant="primary"
                    flat
                    icon="description"
                    rounded="none"
                    full-width
                    align="left"
                    class="whitespace-nowrap"
                    :data-test="getTestId('dropdown-open-json-import-dialog-button')"
                    @click="openJsonImportDialog()"
                  >
                    Medikationsanalyse aus JSON-Datei importieren
                  </MedButton>
                </li>
              </ul>
            </template>
          </ComponentDropdown>
        </div>

        <!-- filter :: start -->
        <div class="mt-4 flex flex-row-reverse items-start justify-between space-x-8 space-x-reverse">
          <form
            v-if="!patient.is_fake && (records.data.length > 0 || form.flags.length > 0)"
            @submit.prevent="submitFilter"
          >
            <ComponentFilterFlags v-model="form.flags" :available-flags="availableFlags" :test-id="getTestId()" />
          </form>

          <!-- ☑️ multiselect actions -->
          <section v-if="selectedRecords?.length" class="flex items-start space-x-1">
            <div
              class="my-2 rounded border-2 border-transparent bg-gray-200 px-1 py-0 text-xs font-semibold text-gray-700"
            >
              {{ selectedRecords?.length }}
            </div>

            <div class="flex flex-wrap gap-0.5">
              <template v-if="selectedRecords.length > 1">
                <MedButton
                  flat
                  icon="mediation"
                  :data-test="getTestId('document-head-bulk-delete-button')"
                  @click="confirmMergeRecord(selectedRecords)"
                >
                  Zusammenführen
                </MedButton>
              </template>

              <MedButton
                flat
                icon="hdr_strong"
                :data-test="getTestId('document-head-bulk-change-button')"
                @click="confirmBulkStatusChange(selectedRecords)"
              >
                Status ändern
              </MedButton>

              <MedButton
                flat
                icon="delete"
                :data-test="getTestId('document-head-bulk-delete-button')"
                @click="confirmDeleteItem(selectedRecords)"
              >
                Löschen
              </MedButton>
            </div>
          </section>
        </div>
        <!-- filter :: end -->

        <!-- table listing :: start -->
        <div class="mt-4">
          <table v-if="records.data.length > 0" class="w-full table-auto">
            <thead>
              <PatientsDocumentHead
                v-model="form.order"
                :selected-rows="selectedRecords"
                :available-rows="availableRecords"
                :test-id="getTestId()"
                @update:selected-rows="(newSelectedRecords) => (selectedRecords = newSelectedRecords)"
                @confirm:bulk-delete="() => confirmDeleteItem(selectedRecords)"
                @confirm:bulk-status-change="() => confirmBulkStatusChange(selectedRecords)"
              />
            </thead>

            <tbody>
              <template v-if="form.processing">
                <tr>
                  <td colspan="8">
                    <div class="grid min-h-32 w-full place-content-center">
                      <MedSpinner size="3xl" :data-test="getTestId('patients-show-spinner')" />
                    </div>
                  </td>
                </tr>
              </template>

              <template v-else>
                <PatientsDocumentRecordRow
                  v-for="(record, index) in records.data"
                  :key="'record-' + record.id"
                  :patient="patient"
                  :record="record"
                  :selected-rows="selectedRecords"
                  :test-id="getTestId(`${index}`)"
                  @open:share-record="openShareRecord(record)"
                  @open:share-status="openShareStatus(record)"
                  @confirm:delete-item="() => confirmDeleteItem([record.id])"
                  @open:edit-record-label="openEditRecordLabel(record)"
                  @update:selected-rows="(newSelectedRecords) => (selectedRecords = newSelectedRecords)"
                />
              </template>
            </tbody>
          </table>

          <template v-else>
            <ComponentEmptyState v-if="form.flags.length > 0" :test-id="getTestId()">
              Für den aktuellen Filter wurden keine Medikationsanalysen gefunden.
            </ComponentEmptyState>

            <ComponentEmptyState v-else class="mr-10 max-w-152" :test-id="getTestId()">
              <div v-if="$page.props?.client?.license?.license_name === 'connect-core'">
                Importieren Sie über Ihre Warenwirtschaft eine erste Medikationsanalyse und ordnen Sie diese dem
                Patienten zu.
              </div>
              <div v-else class="flex items-start">
                <div class="space-y-4">
                  <p>
                    Legen Sie über den Button
                    <span class="font-semibold">„Neue Analyse“</span>
                    eine erste Medikationsanalyse für diesen Patienten an.
                  </p>
                  <p>
                    Sie können eine neue Medikationsanalyse
                    <button
                      class="cursor-pointer text-mcred hover:underline"
                      :data-test="getTestId('create-polymedication')"
                      @click.prevent="createPolymedication()"
                    >
                      manuell eingeben
                    </button>
                    oder einen vorliegenden
                    <button
                      class="cursor-pointer text-mcred hover:underline"
                      :data-test="getTestId('open-bmp-dialog')"
                      @click.prevent="openBmpImportDialog()"
                    >
                      BMP importieren.
                    </button>
                  </p>
                </div>

                <div class="relative -top-6 fill-blue-900">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    xml:space="preserve"
                    viewBox="0 0 100 100"
                    class="size-32 rotate-90"
                  >
                    <path
                      d="M5.78 13.88c3.89-.42 7.89.35 11.76.77l11.91 1.32c1.31.14 2.82 3.76 1.07 3.78-.29 0-.57 0-.86.01-.2 0-.4-.06-.6-.16l-10.27-1.14c-2.27-.25-4.58-.62-6.89-.79 26.97 9.44 53.72 22.63 71.31 45.91 4.78 6.33 8.75 13.24 11.63 20.64.26.68.34 1.88-.6 2.03-.93.15-1.76-1.07-2.04-1.78-10.34-26.5-34.56-44.05-59.69-55.27-6.59-2.94-13.34-5.51-20.16-7.89 1.72 1.81 3.43 3.62 5.15 5.42.55.58 1.08 1.93.52 2.65-.61.8-1.59 0-2.06-.49-2.16-2.28-4.32-4.55-6.48-6.83-1.87-1.97-3.96-3.87-4.45-6.67-.1-.57.01-1.43.75-1.51z"
                    />
                  </svg>
                </div>
              </div>
            </ComponentEmptyState>
          </template>
          <!-- Paginator -->
          <ComponentPagination v-if="!form.processing && records.meta" :links="records.meta.links" />
        </div>
        <!-- table listing :: end -->
      </div>

      <!-- article::aside -->
      <div class="grid w-full grid-cols-1 gap-6 md:grid-cols-2 2xl:w-96 2xl:grid-cols-1">
        <section v-if="$page.props.can.view_todos" class="bg-white p-4 shadow-sm">
          <ComponentTodoList :patient="patient" :text-id="getTestId()" />
        </section>
      </div>
    </article>
  </section>

  <ComponentRecordShareDialog ref="refShareRecordDialog" :patient="patient" />

  <ComponentRecordShareStatusDialog ref="refShareStatusDialog" :patient="patient" />

  <ComponentConfirmationDialog
    ref="refConfirmDeleteItem"
    :title="confirmDeleteTitle"
    :content="confirmDeleteContent"
    @confirmed="(recordIds) => remove(recordIds)"
  />

  <ComponentConfirmationDialog
    ref="refConfirmBulkStatusChange"
    title="Status ändern"
    @confirmed="(recordIds) => bulkStatusChange(recordIds, refBulkStatusChangeStatus)"
  >
    <template #content>
      <component-record-status
        @update:status="(newBulkStatusChangeStatus) => (refBulkStatusChangeStatus = newBulkStatusChangeStatus)"
      />
      <br />
      Der Status
      <template v-if="selectedRecords.length === 1">der Medikationsanalyse</template>
      <template v-else>der Medikationsanalysen ({{ selectedRecords.length }})</template>
      wird angepasst.
    </template>
  </ComponentConfirmationDialog>

  <ComponentBmpStoreDialog
    ref="refBmpStoreDialog"
    :mp-uuid="$page.props?.mpUuid"
    :patient="patient"
    :test-id="getTestId()"
  />

  <ComponentBmpImportDialog ref="refBmpImportDialog" />

  <ComponentJsonImportDialog ref="refJsonImportDialog" title="JSON-Datei importieren" />

  <ComponentRecordEditLabelDialog ref="refEditRecordLabelDialog" />

  <ComponentMergeRecordDialog ref="refMergeRecordDialog" />
</template>

<style scoped>
  button[data-med-state="open"] [data-med-slot="icon"]:last-child {
    transform: rotate(180deg);
  }

  [data-med-slot="icon"]:last-child {
    transition: transform 200ms ease-in-out;
  }
</style>
